/**
 * Движок формул для учебной таблицы (ЕГЭ 3 / 18, ОГЭ 14).
 * Реестр FUNCS — новые функции добавляются туда без переписывания парсера.
 */

const ERR_FORMULA = '#ФОРМУЛА';
const ERR_CYCLE = '#ЦИКЛ';
const ERR_NA = '#Н/Д';
const ERR_VALUE = '#ЗНАЧ!';

const COL_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

export function colLabel(c) {
  if (c < 0) return '?';
  if (c < 26) return COL_LETTERS[c];
  let n = c;
  let s = '';
  while (n >= 0) {
    s = COL_LETTERS[n % 26] + s;
    n = Math.floor(n / 26) - 1;
  }
  return s;
}

export function cellKey(r, c) {
  return `${r},${c}`;
}

/** $A$1 / $A1 / A$1 / A1 → { row, col, absRow, absCol } */
export function parseCellRef(ref) {
  const m = String(ref || '')
    .trim()
    .toUpperCase()
    .match(/^(\$?)([A-Z]+)(\$?)(\d+)$/);
  if (!m) return null;
  let col = 0;
  for (const ch of m[2]) col = col * 26 + (ch.charCodeAt(0) - 64);
  col -= 1;
  const row = Number(m[4]) - 1;
  if (row < 0 || col < 0) return null;
  return {
    row,
    col,
    absCol: m[1] === '$',
    absRow: m[3] === '$',
  };
}

export function formatCellRef({ row, col, absRow = false, absCol = false }) {
  return `${absCol ? '$' : ''}${colLabel(col)}${absRow ? '$' : ''}${row + 1}`;
}

export function expandRange(a, b) {
  const r0 = Math.min(a.row, b.row);
  const r1 = Math.max(a.row, b.row);
  const c0 = Math.min(a.col, b.col);
  const c1 = Math.max(a.col, b.col);
  const out = [];
  for (let r = r0; r <= r1; r += 1) {
    for (let c = c0; c <= c1; c += 1) out.push({ row: r, col: c });
  }
  return out;
}

/** Только столбец: A / $B → { col, absCol } */
export function parseColRef(ref) {
  const m = String(ref || '')
    .trim()
    .toUpperCase()
    .match(/^(\$?)([A-Z]+)$/);
  if (!m) return null;
  let col = 0;
  for (const ch of m[2]) col = col * 26 + (ch.charCodeAt(0) - 64);
  col -= 1;
  if (col < 0) return null;
  return { col, absCol: m[1] === '$' };
}

export function formatColRangeRef(c0, c1) {
  const a = colLabel(Math.min(c0, c1));
  const b = colLabel(Math.max(c0, c1));
  return `${a}:${b}`;
}

/**
 * A1:B2 или целые столбцы A:A / A:C.
 * Для столбцов нужны rows (высота листа), иначе 1000.
 */
export function parseRangeRef(text, opts = {}) {
  const parts = String(text || '').split(':');
  if (parts.length !== 2) return null;
  const a = parseCellRef(parts[0]);
  const b = parseCellRef(parts[1]);
  if (a && b) return { a, b, cells: expandRange(a, b), kind: 'cells' };

  const ca = parseColRef(parts[0]);
  const cb = parseColRef(parts[1]);
  if (ca == null || cb == null) return null;
  const c0 = Math.min(ca.col, cb.col);
  const c1 = Math.max(ca.col, cb.col);
  const rows = Math.max(1, Number(opts.rows) || 1000);
  const aCell = { row: 0, col: c0, absRow: true, absCol: ca.absCol };
  const bCell = { row: rows - 1, col: c1, absRow: true, absCol: cb.absCol };
  return {
    a: aCell,
    b: bCell,
    cells: expandRange(aCell, bCell),
    kind: 'cols',
    c0,
    c1,
  };
}

/** Разбить аргументы функции с учётом кавычек и вложенных скобок. */
export function splitArgs(argStr) {
  const s = String(argStr || '');
  const out = [];
  let cur = '';
  let depth = 0;
  let inStr = false;
  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];
    if (inStr) {
      cur += ch;
      if (ch === '"') {
        if (s[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else {
          inStr = false;
        }
      }
      continue;
    }
    if (ch === '"') {
      inStr = true;
      cur += ch;
      continue;
    }
    if (ch === '(') {
      depth += 1;
      cur += ch;
      continue;
    }
    if (ch === ')') {
      depth = Math.max(0, depth - 1);
      cur += ch;
      continue;
    }
    if (depth === 0 && (ch === ';' || ch === ',')) {
      const t = cur.trim();
      if (t) out.push(t);
      cur = '';
      continue;
    }
    cur += ch;
  }
  const t = cur.trim();
  if (t) out.push(t);
  return out;
}

function normalizeFnName(name) {
  return String(name || '')
    .trim()
    .toUpperCase()
    .replace(/Ё/g, 'Е');
}

function toNumber(v) {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'boolean') return v ? 1 : 0;
  if (v == null || v === '') return null;
  if (typeof v === 'string') {
    const n = Number(v.replace(',', '.'));
    if (Number.isFinite(n) && v.trim() !== '') return n;
  }
  return null;
}

function isBlank(v) {
  return v == null || v === '';
}

function isTruthy(v) {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') {
    const t = v.trim().toUpperCase();
    if (t === 'ИСТИНА' || t === 'TRUE') return true;
    if (t === 'ЛОЖЬ' || t === 'FALSE') return false;
    const n = toNumber(v);
    if (n != null) return n !== 0;
    return v.length > 0;
  }
  return Boolean(v);
}

/** Критерий СЧЁТЕСЛИ / СУММЕСЛИ: ">3500", ">=60", "лыжи", 42, "<>0" */
export function matchCriteria(value, criteriaRaw) {
  let crit = criteriaRaw;
  if (typeof crit === 'string') {
    const q = crit.match(/^"(.*)"$/s);
    if (q) crit = q[1].replace(/""/g, '"');
  }
  if (crit == null) return isBlank(value);

  const critStr = String(crit).trim();
  const opM = critStr.match(/^(<>|<=|>=|<|>|=)(.*)$/);
  if (opM) {
    const op = opM[1];
    let rhs = opM[2].trim();
    if (rhs.startsWith('"') && rhs.endsWith('"')) {
      rhs = rhs.slice(1, -1).replace(/""/g, '"');
    }
    const lhsNum = toNumber(value);
    const rhsNum = toNumber(rhs);
    if (lhsNum != null && rhsNum != null) {
      if (op === '=') return lhsNum === rhsNum;
      if (op === '<>') return lhsNum !== rhsNum;
      if (op === '<') return lhsNum < rhsNum;
      if (op === '>') return lhsNum > rhsNum;
      if (op === '<=') return lhsNum <= rhsNum;
      if (op === '>=') return lhsNum >= rhsNum;
    }
    const lhsS = value == null ? '' : String(value);
    const rhsS = String(rhs);
    const cmp = lhsS.localeCompare(rhsS, undefined, { sensitivity: 'base' });
    if (op === '=') return cmp === 0;
    if (op === '<>') return cmp !== 0;
    if (op === '<') return cmp < 0;
    if (op === '>') return cmp > 0;
    if (op === '<=') return cmp <= 0;
    if (op === '>=') return cmp >= 0;
    return false;
  }

  const cNum = toNumber(critStr);
  const vNum = toNumber(value);
  if (cNum != null && vNum != null) return vNum === cNum;
  const vStr = value == null ? '' : String(value);
  return vStr.localeCompare(String(critStr), undefined, { sensitivity: 'base' }) === 0;
}

function collectFromArg(arg, ctx, mode = 'numbers') {
  const vals = [];
  const range = parseRangeRef(arg, { rows: ctx.rows });
  if (range) {
    for (const ref of range.cells) {
      const v = ctx.evalCell(ref.row, ref.col, ctx.visiting);
      if (mode === 'numbers') {
        const n = toNumber(v);
        if (n != null) vals.push(n);
      } else if (mode === 'nonblank') {
        if (!isBlank(v)) vals.push(v);
      } else {
        vals.push(v);
      }
    }
    return vals;
  }
  const ref = parseCellRef(arg);
  if (ref) {
    const v = ctx.evalCell(ref.row, ref.col, ctx.visiting);
    if (mode === 'numbers') {
      const n = toNumber(v);
      if (n != null) vals.push(n);
    } else if (mode === 'nonblank') {
      if (!isBlank(v)) vals.push(v);
    } else {
      vals.push(v);
    }
    return vals;
  }
  const lit = ctx.evalExpr(arg);
  if (mode === 'numbers') {
    const n = toNumber(lit);
    if (n != null) vals.push(n);
  } else if (mode === 'nonblank') {
    if (!isBlank(lit)) vals.push(lit);
  } else {
    vals.push(lit);
  }
  return vals;
}

function rangeShape(arg, ctx = {}) {
  const range = parseRangeRef(arg, { rows: ctx.rows });
  if (range) {
    return {
      cells: range.cells,
      rows: Math.abs(range.a.row - range.b.row) + 1,
      cols: Math.abs(range.a.col - range.b.col) + 1,
      r0: Math.min(range.a.row, range.b.row),
      c0: Math.min(range.a.col, range.b.col),
    };
  }
  const ref = parseCellRef(arg);
  if (ref) {
    return { cells: [ref], rows: 1, cols: 1, r0: ref.row, c0: ref.col };
  }
  return null;
}

/** Реестр: имя (без ё) → реализация. */
export const FUNCS = {
  СУММ(args, ctx) {
    const nums = args.flatMap((a) => collectFromArg(a, ctx, 'numbers'));
    return nums.reduce((s, n) => s + n, 0);
  },
  SUM(args, ctx) {
    return FUNCS.СУММ(args, ctx);
  },
  МАКС(args, ctx) {
    const nums = args.flatMap((a) => collectFromArg(a, ctx, 'numbers'));
    if (!nums.length) return 0;
    return Math.max(...nums);
  },
  MAX(args, ctx) {
    return FUNCS.МАКС(args, ctx);
  },
  МИН(args, ctx) {
    const nums = args.flatMap((a) => collectFromArg(a, ctx, 'numbers'));
    if (!nums.length) return 0;
    return Math.min(...nums);
  },
  MIN(args, ctx) {
    return FUNCS.МИН(args, ctx);
  },
  СЧЕТ(args, ctx) {
    const nums = args.flatMap((a) => collectFromArg(a, ctx, 'numbers'));
    return nums.length;
  },
  COUNT(args, ctx) {
    return FUNCS.СЧЕТ(args, ctx);
  },
  СЧЕТЗ(args, ctx) {
    return args.flatMap((a) => collectFromArg(a, ctx, 'nonblank')).length;
  },
  COUNTA(args, ctx) {
    return FUNCS.СЧЕТЗ(args, ctx);
  },
  СРЗНАЧ(args, ctx) {
    const nums = args.flatMap((a) => collectFromArg(a, ctx, 'numbers'));
    if (!nums.length) return 0;
    return nums.reduce((s, n) => s + n, 0) / nums.length;
  },
  AVERAGE(args, ctx) {
    return FUNCS.СРЗНАЧ(args, ctx);
  },
  ЕСЛИ(args, ctx) {
    if (args.length < 2) return ERR_FORMULA;
    const cond = ctx.evalExpr(args[0]);
    if (isTruthy(cond)) return ctx.evalExpr(args[1]);
    if (args.length >= 3) return ctx.evalExpr(args[2]);
    return false;
  },
  IF(args, ctx) {
    return FUNCS.ЕСЛИ(args, ctx);
  },
  СЧЕТЕСЛИ(args, ctx) {
    if (args.length < 2) return ERR_FORMULA;
    const shape = rangeShape(args[0], ctx);
    if (!shape) return ERR_FORMULA;
    const crit = ctx.evalExpr(args[1]);
    let n = 0;
    for (const ref of shape.cells) {
      const v = ctx.evalCell(ref.row, ref.col, ctx.visiting);
      if (matchCriteria(v, crit)) n += 1;
    }
    return n;
  },
  COUNTIF(args, ctx) {
    return FUNCS.СЧЕТЕСЛИ(args, ctx);
  },
  СУММЕСЛИ(args, ctx) {
    if (args.length < 2) return ERR_FORMULA;
    const condShape = rangeShape(args[0], ctx);
    if (!condShape) return ERR_FORMULA;
    const crit = ctx.evalExpr(args[1]);
    const sumShape = args.length >= 3 ? rangeShape(args[2], ctx) : condShape;
    if (!sumShape) return ERR_FORMULA;
    let sum = 0;
    const n = Math.min(condShape.cells.length, sumShape.cells.length);
    for (let i = 0; i < n; i += 1) {
      const cv = ctx.evalCell(condShape.cells[i].row, condShape.cells[i].col, ctx.visiting);
      if (!matchCriteria(cv, crit)) continue;
      const sv = toNumber(ctx.evalCell(sumShape.cells[i].row, sumShape.cells[i].col, ctx.visiting));
      if (sv != null) sum += sv;
    }
    return sum;
  },
  SUMIF(args, ctx) {
    return FUNCS.СУММЕСЛИ(args, ctx);
  },
  СРЗНАЧЕСЛИ(args, ctx) {
    if (args.length < 2) return ERR_FORMULA;
    const condShape = rangeShape(args[0], ctx);
    if (!condShape) return ERR_FORMULA;
    const crit = ctx.evalExpr(args[1]);
    const avgShape = args.length >= 3 ? rangeShape(args[2], ctx) : condShape;
    if (!avgShape) return ERR_FORMULA;
    let sum = 0;
    let count = 0;
    const n = Math.min(condShape.cells.length, avgShape.cells.length);
    for (let i = 0; i < n; i += 1) {
      const cv = ctx.evalCell(condShape.cells[i].row, condShape.cells[i].col, ctx.visiting);
      if (!matchCriteria(cv, crit)) continue;
      const sv = toNumber(ctx.evalCell(avgShape.cells[i].row, avgShape.cells[i].col, ctx.visiting));
      if (sv != null) {
        sum += sv;
        count += 1;
      }
    }
    return count ? sum / count : 0;
  },
  AVERAGEIF(args, ctx) {
    return FUNCS.СРЗНАЧЕСЛИ(args, ctx);
  },
  СЧЕТЕСЛИМН(args, ctx) {
    if (args.length < 2 || args.length % 2 !== 0) return ERR_FORMULA;
    const pairs = [];
    for (let i = 0; i < args.length; i += 2) {
      const shape = rangeShape(args[i], ctx);
      if (!shape) return ERR_FORMULA;
      pairs.push({ shape, crit: ctx.evalExpr(args[i + 1]) });
    }
    const len = pairs[0].shape.cells.length;
    if (pairs.some((p) => p.shape.cells.length !== len)) return ERR_VALUE;
    let n = 0;
    for (let i = 0; i < len; i += 1) {
      let ok = true;
      for (const p of pairs) {
        const v = ctx.evalCell(p.shape.cells[i].row, p.shape.cells[i].col, ctx.visiting);
        if (!matchCriteria(v, p.crit)) {
          ok = false;
          break;
        }
      }
      if (ok) n += 1;
    }
    return n;
  },
  COUNTIFS(args, ctx) {
    return FUNCS.СЧЕТЕСЛИМН(args, ctx);
  },
  СУММЕСЛИМН(args, ctx) {
    if (args.length < 3 || (args.length - 1) % 2 !== 0) return ERR_FORMULA;
    const sumShape = rangeShape(args[0], ctx);
    if (!sumShape) return ERR_FORMULA;
    const pairs = [];
    for (let i = 1; i < args.length; i += 2) {
      const shape = rangeShape(args[i], ctx);
      if (!shape) return ERR_FORMULA;
      pairs.push({ shape, crit: ctx.evalExpr(args[i + 1]) });
    }
    const len = sumShape.cells.length;
    if (pairs.some((p) => p.shape.cells.length !== len)) return ERR_VALUE;
    let sum = 0;
    for (let i = 0; i < len; i += 1) {
      let ok = true;
      for (const p of pairs) {
        const v = ctx.evalCell(p.shape.cells[i].row, p.shape.cells[i].col, ctx.visiting);
        if (!matchCriteria(v, p.crit)) {
          ok = false;
          break;
        }
      }
      if (!ok) continue;
      const sv = toNumber(ctx.evalCell(sumShape.cells[i].row, sumShape.cells[i].col, ctx.visiting));
      if (sv != null) sum += sv;
    }
    return sum;
  },
  SUMIFS(args, ctx) {
    return FUNCS.СУММЕСЛИМН(args, ctx);
  },
  ВПР(args, ctx) {
    if (args.length < 3) return ERR_FORMULA;
    const lookup = ctx.evalExpr(args[0]);
    const table = rangeShape(args[1], ctx);
    if (!table) return ERR_FORMULA;
    const colIndex = toNumber(ctx.evalExpr(args[2]));
    if (colIndex == null || colIndex < 1 || colIndex > table.cols) return ERR_NA;
    for (let r = 0; r < table.rows; r += 1) {
      const key = ctx.evalCell(table.r0 + r, table.c0, ctx.visiting);
      const kn = toNumber(key);
      const ln = toNumber(lookup);
      const equal = kn != null && ln != null
        ? kn === ln
        : String(key ?? '').localeCompare(String(lookup ?? ''), undefined, { sensitivity: 'base' }) === 0;
      if (!equal) continue;
      const out = ctx.evalCell(table.r0 + r, table.c0 + colIndex - 1, ctx.visiting);
      return out == null ? '' : out;
    }
    return ERR_NA;
  },
  VLOOKUP(args, ctx) {
    return FUNCS.ВПР(args, ctx);
  },
};

function callFunc(name, argStr, ctx) {
  const key = normalizeFnName(name);
  const impl = FUNCS[key];
  if (!impl) return ERR_FORMULA;
  const args = splitArgs(argStr);
  return impl(args, ctx);
}

/**
 * Парсер выражений: сравнения, +/-, унарный минус, числа, строки, ссылки, вызовы.
 */
export function createExprEvaluator(evalCell, state = null) {
  const rows = Math.max(1, Number(state?.rows) || 1000);
  const cols = Math.max(1, Number(state?.cols) || 50);
  function evalExpr(input, visiting = new Set()) {
    const ctx = {
      evalCell,
      evalExpr: (s) => evalExpr(s, visiting),
      visiting,
      rows,
      cols,
    };
    return parseComparison(String(input || '').trim(), ctx);
  }
  return evalExpr;
}

function parseComparison(s, ctx) {
  const ops = ['<>', '<=', '>=', '<', '>', '='];
  let depth = 0;
  let inStr = false;
  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];
    if (inStr) {
      if (ch === '"') {
        if (s[i + 1] === '"') i += 1;
        else inStr = false;
      }
      continue;
    }
    if (ch === '"') {
      inStr = true;
      continue;
    }
    if (ch === '(') {
      depth += 1;
      continue;
    }
    if (ch === ')') {
      depth = Math.max(0, depth - 1);
      continue;
    }
    if (depth !== 0) continue;
    for (const op of ops) {
      if (s.startsWith(op, i)) {
        // не путать начало формулы; внутри выражения = — сравнение
        const left = s.slice(0, i).trim();
        const right = s.slice(i + op.length).trim();
        if (!left || !right) break;
        const lv = parseAdd(left, ctx);
        const rv = parseAdd(right, ctx);
        const ln = toNumber(lv);
        const rn = toNumber(rv);
        if (ln != null && rn != null) {
          if (op === '=') return ln === rn;
          if (op === '<>') return ln !== rn;
          if (op === '<') return ln < rn;
          if (op === '>') return ln > rn;
          if (op === '<=') return ln <= rn;
          if (op === '>=') return ln >= rn;
        }
        const ls = lv == null ? '' : String(lv);
        const rs = rv == null ? '' : String(rv);
        const cmp = ls.localeCompare(rs, undefined, { sensitivity: 'base' });
        if (op === '=') return cmp === 0;
        if (op === '<>') return cmp !== 0;
        if (op === '<') return cmp < 0;
        if (op === '>') return cmp > 0;
        if (op === '<=') return cmp <= 0;
        if (op === '>=') return cmp >= 0;
        return false;
      }
    }
  }
  return parseAdd(s, ctx);
}

function parseAdd(s, ctx) {
  const parts = splitByOps(s, ['+', '-']);
  if (parts.length === 1) return parseMul(parts[0].term, ctx);
  let acc = parseMul(parts[0].term, ctx);
  for (let i = 1; i < parts.length; i += 1) {
    const right = parseMul(parts[i].term, ctx);
    const ln = toNumber(acc);
    const rn = toNumber(right);
    if (ln == null || rn == null) return ERR_VALUE;
    acc = parts[i].op === '-' ? ln - rn : ln + rn;
  }
  return acc;
}

function parseMul(s, ctx) {
  const parts = splitByOps(s, ['*', '/']);
  if (parts.length === 1) return parseUnary(parts[0].term, ctx);
  let acc = parseUnary(parts[0].term, ctx);
  for (let i = 1; i < parts.length; i += 1) {
    const right = parseUnary(parts[i].term, ctx);
    const ln = toNumber(acc);
    const rn = toNumber(right);
    if (ln == null || rn == null) return ERR_VALUE;
    if (parts[i].op === '/') {
      if (rn === 0) return ERR_VALUE;
      acc = ln / rn;
    } else {
      acc = ln * rn;
    }
  }
  return acc;
}

/** Разбить по операторам на глубине 0; ведущий +/- остаётся в терме. */
function splitByOps(s, ops) {
  const opSet = new Set(ops);
  const parts = [];
  let cur = '';
  let depth = 0;
  let inStr = false;
  let op = ops.includes('+') ? '+' : ops[0];
  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];
    if (inStr) {
      cur += ch;
      if (ch === '"') {
        if (s[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else inStr = false;
      }
      continue;
    }
    if (ch === '"') {
      inStr = true;
      cur += ch;
      continue;
    }
    if (ch === '(') {
      depth += 1;
      cur += ch;
      continue;
    }
    if (ch === ')') {
      depth = Math.max(0, depth - 1);
      cur += ch;
      continue;
    }
    if (depth === 0 && opSet.has(ch)) {
      if (cur.trim() === '' && (ch === '+' || ch === '-')) {
        cur += ch;
        continue;
      }
      parts.push({ op, term: cur.trim() });
      op = ch;
      cur = '';
      continue;
    }
    cur += ch;
  }
  parts.push({ op, term: cur.trim() });
  return parts;
}

function parseUnary(s, ctx) {
  const t = s.trim();
  if (t.startsWith('-')) {
    const v = parseUnary(t.slice(1), ctx);
    const n = toNumber(v);
    if (n == null) return ERR_VALUE;
    return -n;
  }
  if (t.startsWith('+')) return parseUnary(t.slice(1), ctx);
  return parsePrimary(t, ctx);
}

function parsePrimary(s, ctx) {
  const t = s.trim();
  if (!t) return null;

  if (t.startsWith('"')) {
    let out = '';
    for (let i = 1; i < t.length; i += 1) {
      if (t[i] === '"') {
        if (t[i + 1] === '"') {
          out += '"';
          i += 1;
          continue;
        }
        return out;
      }
      out += t[i];
    }
    return ERR_FORMULA;
  }

  if (t.startsWith('(') && t.endsWith(')')) {
    return parseComparison(t.slice(1, -1).trim(), ctx);
  }

  const fnM = t.match(/^([A-Za-zА-Яа-яЁё_]+)\s*\((.*)\)\s*$/s);
  if (fnM) {
    return callFunc(fnM[1], fnM[2], ctx);
  }

  const range = parseRangeRef(t, { rows: ctx.rows });
  if (range) {
    // одиночный диапазон вне функции — ошибка
    return ERR_FORMULA;
  }

  const ref = parseCellRef(t);
  if (ref) return ctx.evalCell(ref.row, ref.col, ctx.visiting);

  // число: 12, 12.5, 12,5, -1E9, 1e-3
  const numStr = t.replace(',', '.');
  if (/^[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?$/.test(numStr)) {
    const n = Number(numStr);
    if (Number.isFinite(n)) return n;
  }

  // именованные константы
  const up = t.toUpperCase().replace(/Ё/g, 'Е');
  if (up === 'ИСТИНА' || up === 'TRUE') return true;
  if (up === 'ЛОЖЬ' || up === 'FALSE') return false;

  return ERR_FORMULA;
}

export function rawCell(state, r, c) {
  const v = state?.cells?.[cellKey(r, c)];
  if (v == null) return '';
  return String(v);
}

export function makeEvaluator(state) {
  const cache = new Map();

  function evalCell(r, c, visiting = new Set()) {
    const key = cellKey(r, c);
    if (cache.has(key)) return cache.get(key);
    if (visiting.has(key)) return ERR_CYCLE;
    const raw = rawCell(state, r, c).trim();
    if (!raw) {
      cache.set(key, null);
      return null;
    }
    if (raw.startsWith('=')) {
      visiting.add(key);
      const body = raw.slice(1).trim();
      const evalExpr = createExprEvaluator(evalCell, state);
      const v = evalExpr(body, visiting);
      visiting.delete(key);
      cache.set(key, v);
      return v;
    }
    const n = Number(raw.replace(',', '.'));
    if (raw !== '' && Number.isFinite(n) && /^-?\d/.test(raw.replace(',', '.'))) {
      cache.set(key, n);
      return n;
    }
    // scientific literals typed without =
    if (/^[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)$/.test(raw.replace(',', '.'))) {
      const sn = Number(raw.replace(',', '.'));
      if (Number.isFinite(sn)) {
        cache.set(key, sn);
        return sn;
      }
    }
    cache.set(key, raw);
    return raw;
  }

  return evalCell;
}

export function displayValue(state, r, c, evalCell) {
  const raw = rawCell(state, r, c);
  if (!raw.startsWith('=')) return raw;
  const v = evalCell(r, c);
  if (v == null) return '';
  if (typeof v === 'boolean') return v ? 'ИСТИНА' : 'ЛОЖЬ';
  if (typeof v === 'number' && Number.isFinite(v)) {
    if (Number.isInteger(v)) return String(v);
    return String(Math.round(v * 1e10) / 1e10);
  }
  return String(v);
}

/**
 * Сдвиг относительных ссылок в формуле (протяжка / копирование).
 * dRow / dCol — на сколько строк/столбцов сдвинуть.
 */
export function shiftFormulaRefs(formula, dRow, dCol) {
  const raw = String(formula || '');
  if (!raw.startsWith('=')) return raw;
  const body = raw.slice(1);
  const re = /(\$?)([A-Za-z]+)(\$?)(\d+)/g;
  const next = body.replace(re, (full, absC, letters, absR, digits, offset) => {
    // не трогать, если это часть имени функции: буква сразу после без цифры уже в match;
    // отсекаем, если перед этим буквы (часть идентификатора) или после буквы без цифры — уже ок
    const before = body[offset - 1];
    if (before && /[A-Za-zА-Яа-яЁё_]/.test(before)) return full;
    const after = body[offset + full.length];
    if (after && /[A-Za-z]/.test(after)) return full;
    const ref = parseCellRef(full);
    if (!ref) return full;
    const row = ref.absRow ? ref.row : ref.row + dRow;
    const col = ref.absCol ? ref.col : ref.col + dCol;
    if (row < 0 || col < 0) return full;
    return formatCellRef({
      row,
      col,
      absRow: ref.absRow,
      absCol: ref.absCol,
    });
  });
  return `=${next}`;
}

export { ERR_FORMULA, ERR_CYCLE, ERR_NA, ERR_VALUE };
